import React from "react";

interface DataPoint {
  feature: string;
  item1: string;
  item2: string;
  icon: string;
  description: string;
  category: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  data?: string | DataPoint[];
  bgColor?: string;
  item1Label?: string;
  item2Label?: string;
  item1Description?: string;
  item2Description?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultData: DataPoint[] = [
  {
    feature: "",
    item1: "",
    item2: "",
    icon: "⭐",
    description: "",
    category: "Category 1",
  },
  {
    feature: "",
    item1: "",
    item2: "",
    icon: "⭐",
    description: "",
    category: "Category 1",
  },
  {
    feature: "",
    item1: "",
    item2: "",
    icon: "⭐",
    description: "",
    category: "Category 2",
  },
  {
    feature: "",
    item1: "",
    item2: "",
    icon: "⭐",
    description: "",
    category: "Category 2",
  },
];

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const ComparisonSlide2 = {
  id: "comparison-slide2",
  name: "Comparison Style 2",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "item1Label",
      label: "Item 1 Label",
      type: "text",
      defaultValue: "",
    },
    {
      id: "item2Label",
      label: "Item 2 Label",
      type: "text",
      defaultValue: "",
    },
    {
      id: "item1Description",
      label: "Item 1 Description",
      type: "text",
      defaultValue: "",
    },
    {
      id: "item2Description",
      label: "Item 2 Description",
      type: "text",
      defaultValue: "",
    },
    {
      id: "data",
      label: "Comparison Data",
      type: "text",
      defaultValue: JSON.stringify(defaultData),
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      bgColor = "linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)",
      data = JSON.stringify(defaultData),
      item1Label = "",
      item2Label = "",
      item1Description = "",
      item2Description = "",
      onEdit = () => {},
    } = content;

    let parsedData = defaultData;
    try {
      if (typeof data === "string") {
        parsedData = JSON.parse(data);
      }
    } catch (error) {
      console.warn("Error parsing data:", error);
    }

    const handleDataEdit = (index: number, field: string, value: string) => {
      const updatedData = [...parsedData];
      updatedData[index] = {
        ...updatedData[index],
        [field]: value,
      };
      onEdit({ ...content, data: JSON.stringify(updatedData) });
    };

    const categories = Array.from(
      new Set(parsedData.map((item) => item.category)),
    );

    // Calculate sizes based on total items and categories
    const totalItems = parsedData.length;

    const getHeaderSize = () => {
      if (totalItems <= 6) return "text-2xl mb-1";
      return "text-xl mb-0.5";
    };

    const getSubtitleSize = () => {
      if (totalItems <= 6) return "text-lg";
      return "text-base";
    };

    const getLabelSize = () => {
      if (totalItems <= 6) return "text-lg";
      return "text-base";
    };

    const getDescriptionSize = () => {
      if (totalItems <= 6) return "text-sm";
      return "text-xs";
    };

    const getPadding = () => {
      if (totalItems <= 6) return "p-2";
      return "p-1.5";
    };

    const getGap = () => {
      if (totalItems <= 6) return "gap-2";
      return "gap-1";
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.1) 75%, transparent 75%, transparent)",
              backgroundSize: "100px 100px",
            }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full p-2">
          {/* Header */}
          <div className="text-center mb-2">
            <EditableDiv
              text={title}
              onEdit={(value) => onEdit({ ...content, title: value })}
              className={`font-bold ${getHeaderSize()} bg-clip-text text-transparent bg-gradient-to-r from-purple-200 to-purple-100`}
            />
            <EditableDiv
              text={subtitle}
              onEdit={(value) => onEdit({ ...content, subtitle: value })}
              className={`${getSubtitleSize()} text-purple-100/90`}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 grid grid-cols-2 gap-2">
            {/* Left Column - Before */}
            <div className="flex flex-col">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 mb-2 text-center">
                <EditableDiv
                  text={item1Label}
                  onEdit={(value) => onEdit({ ...content, item1Label: value })}
                  className={`${getLabelSize()} font-semibold text-purple-200 mb-0.5`}
                />
                <EditableDiv
                  text={item1Description}
                  onEdit={(value) =>
                    onEdit({ ...content, item1Description: value })
                  }
                  className={`${getDescriptionSize()} text-purple-100/80`}
                />
              </div>
              <div className={`flex-1 grid auto-rows-fr ${getGap()}`}>
                {categories.map((category) => {
                  const categoryItems = parsedData.filter(
                    (item) => item.category === category,
                  );
                  return categoryItems.map((item, index) => (
                    <div
                      key={`${category}-${index}`}
                      className={`bg-white/10 backdrop-blur-sm rounded-lg ${getPadding()} transform transition-all duration-300 hover:bg-white/15`}
                    >
                      <div className="flex items-center gap-1 mb-1">
                        <span className={getLabelSize()}>{item.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs text-purple-200/80 mb-0.5`}>
                            {category}
                          </div>
                          <EditableDiv
                            text={item.feature}
                            onEdit={(value) =>
                              handleDataEdit(index, "feature", value)
                            }
                            className={`font-semibold ${getLabelSize()} truncate`}
                          />
                        </div>
                      </div>
                      <div className="bg-white/5 rounded p-1.5 transition-all duration-300 hover:bg-white/10">
                        <EditableDiv
                          text={item.item1}
                          onEdit={(value) =>
                            handleDataEdit(index, "item1", value)
                          }
                          className={`${getLabelSize()} text-center truncate`}
                        />
                      </div>
                    </div>
                  ));
                })}
              </div>
            </div>

            {/* Right Column - After */}
            <div className="flex flex-col">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 mb-2 text-center">
                <EditableDiv
                  text={item2Label}
                  onEdit={(value) => onEdit({ ...content, item2Label: value })}
                  className={`${getLabelSize()} font-semibold text-purple-200 mb-0.5`}
                />
                <EditableDiv
                  text={item2Description}
                  onEdit={(value) =>
                    onEdit({ ...content, item2Description: value })
                  }
                  className={`${getDescriptionSize()} text-purple-100/80`}
                />
              </div>
              <div className={`flex-1 grid auto-rows-fr ${getGap()}`}>
                {categories.map((category) => {
                  const categoryItems = parsedData.filter(
                    (item) => item.category === category,
                  );
                  return categoryItems.map((item, index) => (
                    <div
                      key={`${category}-${index}`}
                      className={`bg-white/10 backdrop-blur-sm rounded-lg ${getPadding()} transform transition-all duration-300 hover:bg-white/15`}
                    >
                      <div className="flex items-center gap-1 mb-1">
                        <span className={getLabelSize()}>{item.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs text-purple-200/80 mb-0.5`}>
                            {category}
                          </div>
                          <EditableDiv
                            text={item.feature}
                            onEdit={(value) =>
                              handleDataEdit(index, "feature", value)
                            }
                            className={`font-semibold ${getLabelSize()} truncate`}
                          />
                        </div>
                      </div>
                      <div className="bg-white/5 rounded p-1.5 transition-all duration-300 hover:bg-white/10">
                        <EditableDiv
                          text={item.item2}
                          onEdit={(value) =>
                            handleDataEdit(index, "item2", value)
                          }
                          className={`${getLabelSize()} text-center truncate`}
                        />
                      </div>
                    </div>
                  ));
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
};

export default ComparisonSlide2;
